/**
 * How each enemy type reads at a glance (Kevin, 2026-09: "enemies need to be clear so we can see the type of them").
 *
 * An enemy's look is a ROLE — its glyph on the overhead badge and the ground ring, its colour, its size — chosen from
 * the archetype's balance data, never from the level. Known ids map to their role directly; an id this table has
 * never seen (balance adds types) is classified from its stats, so a new archetype arrives with a sensible badge
 * instead of none. The glyph's SHAPE carries the meaning and the colour only reinforces it, which is what keeps the
 * colour-blind palette honest (P1.7.4).
 */
import type { BalanceData, Enemy, EnemyArchetype } from "@highmarch/domain";
import { BossMechanic } from "@highmarch/domain";

export type EnemyRole = "raider" | "runner" | "brute" | "shield" | "healer" | "flyer" | "bomber" | "boss"
  // the M21 enemies (docs/ENEMIES_V2.md): each its own role, its own glyph
  | "summoner" | "imp" | "burrower" | "spellward" | "stunner";

/** An elite's affix (DESIGN §5.1), drawn as the first status icon beside the badge for as long as it lives. */
export type AffixStatus = "warded" | "fireproof" | "frostbound" | "splitting";
/** Status reads drawn beside the badge (order = priority when more than two apply); an affix always comes first. */
export type EnemyStatus = "slowed" | "burning" | "armour" | "shred" | "boosted" | "shielded" | AffixStatus
  // ENEMIES_V2: a Burrower underground (no tower can find it), a Summoner casting or a Stunner winding up (punish it now)
  | "burrowed" | "casting"
  // M36: an immune unit's immunity, held beside the badge for as long as it lives (like an affix)
  | ImmuneStatus;
/** M36 immunities as plate statuses (rare/rareApi.ts Immunity). */
export type ImmuneStatus = "immPhysical" | "immMagic" | "immFire" | "immSlow";

/** Affix ids of today's balance → their icon. An affix this table has never seen shows none (its role badge still reads). */
const AFFIX_STATUS: Readonly<Record<string, AffixStatus>> = { warded: "warded", fireproof: "fireproof", frostbound: "frostbound", splitting: "splitting" };

/** The affix icon an enemy wears, or null for a plain one. */
export function affixStatus(balance: BalanceData, e: Enemy): AffixStatus | null {
  return e.affix >= 0 ? AFFIX_STATUS[balance.affixes[e.affix]?.id ?? ""] ?? null : null;
}

export interface EnemyLook {
  readonly role: EnemyRole;
  /** Badge and ring colour (0xRRGGBB) for the current palette. */
  readonly colour: number;
  /** Model scale on top of the art's own: runners read small and quick, brutes big. */
  readonly scale: number;
  /** Strength of the body's colour wash (emissive), 0–1 of `colour`. */
  readonly wash: number;
}

/** Ids of today's archetypes → role. Anything else goes through `inferRole`. */
const ROLE_BY_ID: Readonly<Record<string, EnemyRole>> = {
  grunt: "raider", runner: "runner", brute: "brute", shieldbearer: "shield", healer: "healer", flyer: "flyer", bomber: "bomber",
  summoner: "summoner", imp: "imp", burrower: "burrower", spellward: "spellward", stunner: "stunner",
};

/** Stat thresholds for classifying an archetype nobody named here (base values, before stage power). */
const INFER = { armourShield: 0.3, speedRunner: 1.5, hpBrute: 200 } as const;

/** Normal palette: hues far apart on the wheel and bright enough to sit on grass, sand, snow and ash. */
const COLOUR: Readonly<Record<EnemyRole, number>> = {
  raider: 0xe8543f, runner: 0xffd23f, brute: 0xff8c1a, shield: 0x4a8dff, healer: 0x2ee6b0, flyer: 0xb58cff, bomber: 0x7d8796, boss: 0xc0142f,
  summoner: 0xe04cff, imp: 0xff7aa8, burrower: 0xa8794a, spellward: 0x4fe0ff, stunner: 0xc2e03a,
};
/** Colour-blind palette (Okabe–Ito): separable under protan/deutan/tritan; the glyphs still carry the type. */
const COLOUR_CB: Readonly<Record<EnemyRole, number>> = {
  raider: 0xd55e00, runner: 0xf0e442, brute: 0xe69f00, shield: 0x0072b2, healer: 0x56b4e9, flyer: 0xcc79a7, bomber: 0x999999, boss: 0x882255,
  // past Okabe–Ito's eight: Tol's muted set; the glyph shapes carry the type here more than ever
  summoner: 0xaa4499, imp: 0xee99aa, burrower: 0x997700, spellward: 0x44aa99, stunner: 0xbbcc33,
};
/**
 * Model scale per role; bosses already ship at boss size, so they only gain a little. Flyers get a little more than
 * a raider: they hover a cell's height up, where the camera sees them smaller and blurred by their bob, and at 1 they
 * read as small pink clumps.
 */
const SCALE: Readonly<Record<EnemyRole, number>> = {
  raider: 1, runner: 0.82, brute: 1.32, shield: 1.08, healer: 0.95, flyer: 1.15, bomber: 1.05, boss: 1.12,
  // on a stand-in (enemyStandIns.ts): the imp is the smallest thing on the road; the stunner nearly a brute
  summoner: 1.04, imp: 0.6, burrower: 1.0, spellward: 1.1, stunner: 1.24,
};
/**
 * The M21 raiders' own models are built at their size on the road (art/unitkit/README.md: the imp 0.205 high, the
 * stunner brute-sized at 0.54), so once they ship they are not shrunk or grown again as their stand-ins were (an imp
 * at ×0.6 read as a red speck under its plate, a stunner at ×1.24 towered over the brutes).
 */
const OWN_ART_SCALE: Readonly<Partial<Record<EnemyRole, number>>> = { imp: 1, stunner: 1 };
/**
 * Body wash (emissive, added light) of a regular enemy: none. At 0.22 (M21–2026-10) a pastel role colour over the whole
 * body hid the painted Horde (crimson, iron, green skin) at play size and washed the shieldbearer blue and the spellward
 * cyan, which the Horde never wears (enemy review 2026-10-27, Q1). The role reads from the badge's glyph and the ground
 * ring; the body keeps its own colours. A boss has none either (WASH_BOSS); a rare sets its own faint one
 * (showpiece.ts RARE_WASH).
 */
export const WASH = 0;
/**
 * A boss's wash: none either (rares/bosses review 2026-10-27). The boss crimson at 0.12 turned every boss into the same
 * red silhouette (the Colossus's basalt pink, the Plague Lord's green red); each now keeps its own palette and wears a
 * modest rim in its own signature colour (BOSS_RIM) under the boss dusk and its rune ring.
 */
export const WASH_BOSS = 0;

/** A boss's rim (UnitLook rimColor / rimStrength / rimPulse): its signature colour, modest; the phase change still flares it. */
export interface BossRim { readonly color: number; readonly strength: number; readonly pulse: number }
const BOSS_RIM_STRENGTH = 0.22, BOSS_RIM_PULSE = 0.08;
const BOSS_RIM_COLOUR: Readonly<Record<string, number>> = {
  boss_warlord: 0xff6a24,       // ember
  boss_ironclad: 0xff7a30,      // furnace ember
  boss_colossus: 0xff4a12,      // lava
  boss_necromancer: 0xffcc55,   // soul-gold
  boss_harpy_queen: 0xe8283c,   // crimson
  boss_plague_lord: 0x86e050,   // plague green
};
/** Any other boss-flagged spawn (a level's boss can be any archetype): the old team ember. */
const BOSS_RIM_DEFAULT = 0xff5a3c;

export function bossRim(archId: string): BossRim {
  return { color: BOSS_RIM_COLOUR[archId] ?? BOSS_RIM_DEFAULT, strength: BOSS_RIM_STRENGTH, pulse: BOSS_RIM_PULSE };
}

export function inferRole(a: EnemyArchetype): EnemyRole {
  const known = ROLE_BY_ID[a.id];
  if (known) return known;
  if (a.rareChapter > 0) return "boss";                    // M36 rares: drawn as bosses (no brute tint or scale), shone gold
  if (a.bossMechanic !== BossMechanic.None || a.id.startsWith("boss_")) return "boss";
  if (a.bombDisableTicks > 0) return "bomber";
  if (a.isAir) return "flyer";
  if (a.healRadius.isPositive()) return "healer";
  if (a.shieldRadius.isPositive()) return "shield";
  if (a.armour.toNumber() >= INFER.armourShield) return "shield";
  if (a.speedCellsPerSecond.toNumber() >= INFER.speedRunner) return "runner";
  if (a.hp.toNumber() >= INFER.hpBrute) return "brute";
  return "raider";
}

export function colourBlind(): boolean { return typeof document !== "undefined" && document.body.classList.contains("colour-blind"); }

export function roleColour(role: EnemyRole, cb = colourBlind()): number { return (cb ? COLOUR_CB : COLOUR)[role]; }

/** The look of one archetype; `boss` is the spawn's own flag (a level's boss can be any archetype). */
export function enemyLook(a: EnemyArchetype, boss = false, ownArt = false): EnemyLook {
  const role = boss ? "boss" : inferRole(a);
  const scale = (ownArt ? OWN_ART_SCALE[role] : undefined) ?? SCALE[role];
  return { role, colour: roleColour(role), scale, wash: role === "boss" ? WASH_BOSS : WASH };
}

/** Short label per role for the HUD's accessible text. */
export const ROLE_LABEL: Readonly<Record<EnemyRole, string>> = {
  raider: "raiders", runner: "runners", brute: "brutes", shield: "shieldbearers", healer: "healers", flyer: "flyers", bomber: "bombers", boss: "boss",
  summoner: "summoners", imp: "imps", burrower: "burrowers", spellward: "spellwards", stunner: "stunners",
};

/** Status tint per status (the glyph shape is what distinguishes them). */
export const STATUS_COLOUR: Readonly<Record<EnemyStatus, number>> = {
  slowed: 0x9fe4ff, burning: 0xff8a3a, armour: 0xd8dde6, shred: 0xff5a7a, boosted: 0xfff07a, shielded: 0x8fb4ff,
  warded: 0xc9d3e0, fireproof: 0xff5a2a, frostbound: 0x7fd8ff, splitting: 0xffd23f,
  burrowed: 0xc9a27a, casting: 0xff6a4a,
  immPhysical: 0xe8d6b0, immMagic: 0xc49cff, immFire: 0xff8a3a, immSlow: 0x9fe8ff,
};
/** The plate status for each immunity kind (rareApi.ts ImmunityKind). */
export const IMMUNE_STATUS: Readonly<Record<"physical" | "magic" | "fire" | "slow", ImmuneStatus>> = { physical: "immPhysical", magic: "immMagic", fire: "immFire", slow: "immSlow" };

/**
 * The radius (cells) of an archetype's effect on its neighbours — a healer's heal, a shieldbearer's shield wall — drawn
 * as a faint aura ring on the ground so the mechanic is visible; 0 for none.
 */
export function auraRadius(a: EnemyArchetype): number { return Math.max(a.healRadius.toNumber(), a.shieldRadius.toNumber()); }
